import React, { useEffect, useState } from "react";
import { FieldArray } from "formik";

import {
    Button,
    FormControl,
    InputLabel,
    Select,
    Input,
    TextField,
    MenuItem,
    FormHelperText,
    Grid,
    Paper,
    IconButton,
    InputAdornment,
    Box,
    CircularProgress,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { dateEquators, integerEquators, StringEquators } from "../../../store/constants/pages";
import { Autocomplete } from '@mui/material';
import { RULE_CRITERIA_DATE, RULE_CRITERIA_INT, RULE_CRITERIA_LONG, RULE_CRITERIA_STRING } from "../../../store/constants/contract";
import { PopoverLink } from '../../../common/popoverLink'
import { FeeScheduleSelection } from "../../feeSchedulePage/feeScheduleSelectionComponent";
import GlobalFeeScheduleFields from "./GlobalFeeScheduleFields";
import { getFeescheduleRateLabel } from '../../../store/api/feeScheduleItems';

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        width: '85%',
        marginLeft: theme.spacing(1),
    },
    portlet: {
        'box-shadow': 'none',
    },
    formControl: {
        width: '85%',
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(1),
    },
    autoComplete: {
        width: '85%',
        marginLeft: theme.spacing(1),
    },
    button: {
        margin: theme.spacing(1)
    },
    paper: {
        marginTop: theme.spacing(2),
    },
    iconButton: {
        marginTop: theme.spacing(3),
    },
    clearButton: {
        marginTop: theme.spacing(3),
        marginLeft: '10px',
        color: 'rgba(0, 0, 0, 0.54)'
    },
}));

export default function RuleLesserOfFieldArray({
    values, handleBlur, handleChange, touched, errors, setFieldValue, setFieldError, disableLesserOfSection=false, getFeeSchedule, getFeeScheduleLabels, handleHoverFeeSchedule,
    popoverLeave, rateLabelValidationErrors, setRateLabelValidationErrors
}) {

    const classes = useStyles();
    const [groupFeeScheduleType, setGroupFeeScheduleType] = useState<'local' | 'global'>(
        values.useGlobalFsForLesserOf ? 'global' : 'local'
    );
    const [validatingRateLabel, setValidatingRateLabel] = useState<Record<number, boolean>>({});

    useEffect(() => {
        setGroupFeeScheduleType(values.useGlobalFsForLesserOf ? 'global' : 'local');
    }, [values.useGlobalFsForLesserOf]);

    function getHelperText(key, index) {
        if (touched.lesserOfConfiguration) {
            if (touched.lesserOfConfiguration[index]) {
                return touched.lesserOfConfiguration[index][key];
            }
            return false;
        }
        return false;
    }

    function getError(key, index) {
        if (errors.lesserOfConfiguration) {
            if (errors.lesserOfConfiguration[index]) {
                return errors.lesserOfConfiguration[index][key];
            }
            return false;
        }
        return false;
    }



    const handleFeeScheduleSelect = (groupIndex: number, rowData: any) => {
        const prefix = `lesserOfConfiguration[${groupIndex}]`;
        const isDMESchedule = rowData?.name?.toLowerCase()?.includes("dme")
        const isGlobalSchedule = rowData.isGlobal || rowData.isRBRVS ; 
        const isRBRVSSchedule = Boolean(rowData.isRBRVS);
        const showGlobalConfig = isGlobalSchedule && (isRBRVSSchedule || isDMESchedule);

        // Auto-set toggle based on selected fee schedule type
        if (isGlobalSchedule) {
            setGroupFeeScheduleType('global');
            setFieldValue('useGlobalFsForLesserOf', true);
        } else {
            setGroupFeeScheduleType('local');
            setFieldValue('useGlobalFsForLesserOf', false);
        }

        if (isGlobalSchedule) {
            setFieldValue(`${prefix}.globalFeeScheduleId`, rowData.id);
            setFieldValue(`${prefix}.globalFeeSchedule`, rowData);
            setFieldValue(`${prefix}.feeScheduleName`, rowData.name);
            setFieldValue(`${prefix}.rateLabels`, rowData.globalFeescheduleLabelToRateXRef || []);
            setFieldValue(`${prefix}.feeScheduleId`, null);
            setFieldValue(`${prefix}.feeSchedule`, null);
            setFieldValue(`${prefix}.feescheduleLabelToRateXRefId`, null);
        } else {
            setFieldValue(`${prefix}.feeScheduleId`, rowData.id);
            setFieldValue(`${prefix}.feeScheduleName`, rowData.name);
            setFieldValue(`${prefix}.feeSchedule`, rowData);
            // Don't set empty rateLabels here - they will be fetched via getFeeScheduleLabels callback below
            setFieldValue(`${prefix}.globalFeeScheduleId`, null);
            setFieldValue(`${prefix}.globalFeeSchedule`, null);
            setFieldValue(`${prefix}.globalFeescheduleLabelToRateXRefId`, null);
        }

        setFieldValue(`${prefix}.requiresGlobalConfig`, showGlobalConfig);

        if (showGlobalConfig) {
            setFieldValue(`${prefix}.selectedRateType`, null);
            setFieldValue(`${prefix}.selectedState`, null);
            setFieldValue(`${prefix}.selectedLocality`, null);
            setFieldValue(`${prefix}.selectedGpci`, null);
            setFieldValue(`${prefix}.global_schedule_rate_column`, null);
        } else {
            setFieldValue(`${prefix}.selectedRateType`, null);
            setFieldValue(`${prefix}.selectedState`, null);
            setFieldValue(`${prefix}.selectedLocality`, null);
            setFieldValue(`${prefix}.selectedGpci`, null);
            setFieldValue(`${prefix}.globalFeescheduleState`, null);
            setFieldValue(`${prefix}.globalFeescheduleLocality`, null);
            setFieldValue(`${prefix}.globalFeescheduleGPCI`, null);
            setFieldValue(`${prefix}.global_schedule_rate_column`, null);
        }

        if (!isGlobalSchedule && getFeeSchedule) {
            getFeeSchedule(rowData.id, (feeSchedule: any) => {
                setFieldValue(`${prefix}.feeSchedule`, feeSchedule);
            });
        }

        if (getFeeScheduleLabels) {
            const rateLabelCallback = (newFeeScheduleLabels: any) => {
                setFieldValue(`${prefix}.rateLabels`, newFeeScheduleLabels);
            };
            getFeeScheduleLabels(rowData.id, rateLabelCallback, null, setFieldValue, isGlobalSchedule);
        }
    };

    return (
        (<FieldArray
                name="lesserOfConfiguration"
                render={({ remove, insert }) => (
                    <Grid container>
                        <Grid item xs={12}>
                            {values.lesserOfConfiguration?.map((lesserOfConfig, index) =>
                                {
                                    const groupErrors = errors?.lesserOfConfiguration?.[index];
                                    const groupTouched = touched?.lesserOfConfiguration?.[index];
                                    return (
                                    <Grid container>
                                        {!disableLesserOfSection && <Grid item xs={3}>
                                            <FormControl className={classes.formControl}>
                                                <InputLabel htmlFor={`lesserOfConfiguration.${index}.lesserOfType`}>Charge / Fee Schedule</InputLabel>
                                                <Select
                                                value={lesserOfConfig?.lesserOfType}
                                                onChange={handleChange}
                                                error={Boolean(getHelperText('lesserOfType', index) && getError('lesserOfType', index))}
                                                input={<Input id={`lesserOfConfiguration.${index}.lesserOfType`} />}
                                                inputProps={{
                                                    name: `lesserOfConfiguration.${index}.lesserOfType`,
                                                    id: `lesserOfConfiguration.${index}.lesserOfType`
                                                }}
                                                >
                                                <MenuItem key={`lesserOfConfiguration.${index}.lesserOfType.Charge`} value={"Charge"}> Charge</MenuItem>
                                                <MenuItem key={`lesserOfConfiguration.${index}.lesserOfType.FeeSchedule`} value={"FeeSchedule"}> FeeSchedule</MenuItem>
                                                </Select>
                                                <FormHelperText error>{getHelperText('lesserOfType', index) && getError('lesserOfType', index)}</FormHelperText>
                                            </FormControl>
                                        </Grid>}
                                        {lesserOfConfig.lesserOfType && lesserOfConfig.lesserOfType === "FeeSchedule" && <>
                                        <Grid item xs={3} className="mt-4">
                                        <FormControl fullWidth size="small">
                                                <InputLabel className="MuiInputLabel-shrink" style={{ fontSize: 11, marginTop: -30, marginLeft: 5 }}>
                                                    Fee Schedule *
                                                </InputLabel>
                                                <Box
                                                    // Only attach hover handlers for LOCAL schedules
                                                    onMouseEnter={ (event) =>
                                                            handleHoverFeeSchedule && handleHoverFeeSchedule(
                                                                event.currentTarget,
                                                                lesserOfConfig?.feeScheduleId || lesserOfConfig?.globalFeeScheduleId,
                                                                lesserOfConfig?.feeSchedule || lesserOfConfig?.globalFeeSchedule,
                                                                lesserOfConfig?.globalFeeScheduleId,
                                                                lesserOfConfig?.globalFeeScheduleId ? { state: lesserOfConfig?.selectedState, gpci: lesserOfConfig?.selectedGpci, locality: lesserOfConfig?.selectedLocality } : null
                                                            )
                                                    }
                                                    onMouseLeave={popoverLeave}
                                                    onClick={popoverLeave}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiChip-root': {
                                                            maxWidth: '100% !important'
                                                        },
                                                        '& .MuiChip-label': {
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            display: 'block'
                                                        }
                                                    }}
                                                    marginTop={1}
                                                >
                                                    <FeeScheduleSelection
                                                        value={
                                                            (lesserOfConfig?.feeScheduleId || lesserOfConfig?.globalFeeScheduleId)
                                                                ? lesserOfConfig?.feeScheduleName
                                                                : ''
                                                        }
                                                        defaultValue='Search Fee Schedule'
                                                        feeScheduleType={groupFeeScheduleType === 'global' ? 'global' : 'local'}
                                                        hideTypeToggle={true}
                                                        handleSetFeeSchedule={(rowData: any) =>
                                                            handleFeeScheduleSelect(index, rowData)
                                                        }
                                                    />
                                                </Box>
                                                <FormHelperText error>
                                                    {groupErrors?.feeScheduleId && groupTouched?.feeScheduleId
                                                        ? groupErrors.feeScheduleId
                                                        : ' '}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                        {(lesserOfConfig?.feeScheduleId || lesserOfConfig?.globalFeeScheduleId) && (
                                            <Grid item xs={2}>
                                                <FormControl className={classes.formControl}
                                                >
                                                    <InputLabel>Rate Label *</InputLabel>
                                                    <Select
                                                        size="small"
                                                        name={groupFeeScheduleType === 'global'
                                                            ? `lesserOfConfiguration.${index}.globalFeescheduleLabelToRateXRefId`
                                                            : `lesserOfConfiguration.${index}.feescheduleLabelToRateXRefId`}
                                                        value={(groupFeeScheduleType === 'global'
                                                            ? lesserOfConfig?.globalFeescheduleLabelToRateXRefId
                                                            : lesserOfConfig?.feescheduleLabelToRateXRefId) || ''}
                                                        onChange={async (e) => {
                                                            handleChange(e);
                                                            // Only validate for LOCAL fee schedules, NOT global
                                                            if (e.target.value && groupFeeScheduleType !== 'global') {
                                                                const feeScheduleId = lesserOfConfig.feeScheduleId;
                                                                const labelId = Number(e.target.value);

                                                                // Set loading state
                                                                setValidatingRateLabel(prev => ({
                                                                    ...prev,
                                                                    [index]: true
                                                                }));

                                                                try {
                                                                    // Call the API directly (not the Redux action)
                                                                    const hasNoValue = await getFeescheduleRateLabel(
                                                                        feeScheduleId,
                                                                        labelId
                                                                    );

                                                                    const fieldName = `lesserOfConfiguration.[${index}].feescheduleLabelToRateXRefId`;

                                                                    // Update state to track validation errors
                                                                    setRateLabelValidationErrors(prev => ({
                                                                        ...prev,
                                                                        [index]: hasNoValue
                                                                    }));

                                                                    if (hasNoValue) {
                                                                        // API returned true = rate has no value = error
                                                                        setFieldError(fieldName, 'This rate label has no value');
                                                                    } else {
                                                                        // API returned false = rate has a value = clear error
                                                                        setFieldError(fieldName, undefined);
                                                                    }
                                                                } catch (error) {
                                                                    // Silently handle validation errors
                                                                } finally {
                                                                    // Clear loading state
                                                                    setValidatingRateLabel(prev => ({
                                                                        ...prev,
                                                                        [index]: false
                                                                    }));
                                                                }
                                                            } else {
                                                                // Clear validation error for this group
                                                                setRateLabelValidationErrors(prev => ({
                                                                    ...prev,
                                                                    [index]: false
                                                                }));
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            handleBlur(e);
                                                            // Re-apply the error if validation state says there's an error
                                                            // This prevents Formik from clearing the error on blur
                                                            if (rateLabelValidationErrors[index]) {
                                                                const fieldName = `lesserOfConfiguration.[${index}].feescheduleLabelToRateXRefId`;
                                                                setFieldError(fieldName, 'This rate label has no value');
                                                            }
                                                        }}
                                                        label="Rate Label"
                                                    >
                                                        <MenuItem value="">
                                                            <em>Select Rate Label</em>
                                                        </MenuItem>
                                                        {lesserOfConfig?.rateLabels?.map((label: any) => (
                                                            <MenuItem key={label.id} value={label.id}>
                                                                {label.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {/* {validatingRateLabel[index] && (
                                                        <CircularProgress
                                                            size={20}
                                                            sx={{
                                                                position: 'absolute',
                                                                right: 32,
                                                                top: '50%',
                                                                marginTop: '-10px',
                                                                zIndex: 1
                                                            }}
                                                        />
                                                    )} */}
                                                    <FormHelperText error>
                                                        {(groupErrors?.feescheduleLabelToRateXRefId && groupTouched?.feescheduleLabelToRateXRefId)
                                                            ? groupErrors.feescheduleLabelToRateXRefId
                                                            : ' '}
                                                    </FormHelperText>
                                                </FormControl>
                                            </Grid>
                                        )}
                                        </>
                                        }

                                        <Grid item xs={3}>
                                            <TextField
                                                label="Percentage"
                                                name={`lesserOfConfiguration.${index}.percentage`}
                                                className={classes.textField}
                                                margin="normal"
                                                type="number"
                                                disabled={lesserOfConfig?.lesserOfType === ''}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={lesserOfConfig?.percentage}
                                                helperText={getHelperText('percentage', index) && getError('percentage', index)}
                                                error={Boolean(getHelperText('percentage', index) && getError('percentage', index))}
                                            />
                                        </Grid>
                                        
                                        {groupFeeScheduleType === 'global' && lesserOfConfig?.globalFeeScheduleId && (
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid item xs={12}>
                                                    <GlobalFeeScheduleFields
                                                        scheduleId={lesserOfConfig.globalFeeScheduleId}
                                                        isRBRVS={lesserOfConfig.requiresGlobalConfig}
                                                        values={{
                                                            selectedRateType: lesserOfConfig.selectedRateType || null,
                                                            selectedState: lesserOfConfig.selectedState || null,
                                                            selectedLocality: lesserOfConfig.selectedLocality || null,
                                                            selectedGpci: lesserOfConfig.selectedGpci || null,
                                                            isDME:lesserOfConfig?.feeScheduleName?.toLowerCase()?.includes("dme")


                                                        }}
                                                        onChange={(field, value) => {
                                                            setFieldValue(`lesserOfConfiguration.${index}.${field}`, value);
                                                        }}
                                                        errors={{
                                                            selectedRateType: (groupTouched?.selectedRateType && groupErrors?.selectedRateType) || '',
                                                            selectedState: (groupTouched?.selectedState && groupErrors?.selectedState) || '',
                                                            selectedLocality: (groupTouched?.selectedLocality && groupErrors?.selectedLocality) || '',
                                                            selectedGpci: (groupTouched?.selectedGpci && groupErrors?.selectedGpci) || ''
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}
                                        
                                        {/* <Grid item xs={3}>
                                            <Grid container>
                                            {(index >= 0 && values.lesserOfConfiguration.length > 1) && (
                                                <IconButton
                                                    edge="start"
                                                    aria-label="Delete"
                                                    onClick={() => remove(index)}
                                                    className={classes.iconButton}
                                                    size="large">
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                            {index === 0 && (
                                                <>
                                                {!disableLesserOfSection &&  <IconButton
                                                    edge="start"
                                                    color="primary"
                                                    aria-label="Add"
                                                    onClick={() => insert(values.lesserOfConfiguration.length, {
                                                        ruleSelectionCriteriaId: null,
                                                        equator: '',
                                                        value: ''
                                                    })}
                                                    className={classes.iconButton}
                                                    size="large">
                                                    <AddCircleIcon />
                                                </IconButton>}
                                                </>
                                            )}
                                            </Grid>
                                        </Grid> */}
                                        
                                    </Grid>
                                    )
                                }
                            )}
                        </Grid>
                    </Grid>
                )}
            />)
    );
}
